import React from 'react';
import { manualDesign, MANUAL_OBJECTIVES } from '../../utils/optimizerObjectives';
import { META_OPTIMIZER_ENABLED } from '../../utils/portfolioDesign';
import Button from '../ui/Button';
import Select from '../ui/Select';
import { RuleLabel, RuleValue, ruleModeControl, ruleSwitchedOff } from './OptimizerRuleTableParts';
import RuleLimitInput from './RuleLimitInput';
import LimitTrack from './LimitTrack';
import RuleStatusChip from './RuleStatusChip';
import { ruleEditState } from './optimizerRuleRegistry';

const FLOOR_RULE = { unit: 'usd', form: 'min' };
const FLOOR_LABEL = 'Risk-weighted Alpha-Score floor';

// Extra construction constraints use the same rows and editor state as the
// existing portfolio limits. Dollars are converted only at the wire boundary.
export function constructionLimitCount(design) {
    if (!design || !META_OPTIMIZER_ENABLED) return 0;
    const m = design.mandate;
    return Number(Boolean(m.score_floor)) + Number(hasAdditionalProtections(m));
}

function hasAdditionalProtections(m) {
    return Boolean(m.position_band_pct != null || m.preserve_sides || m.preserve_gross || m.preserve_net
        || m.position_bands.length || m.factor_targets.length || m.baskets.length || m.hedge_sleeve || m.total_vol_max_pct_aum != null);
}

/**
 * The construction rows of a rule table (PortfolioRulesTable, and
 * ActiveLimitsTable with `activeOnly`, which adds the "Construction" scope
 * cell and shows the floor only while it is on): the Alpha-Score floor row
 * and, when the saved portfolio carries other protections, a full-width row
 * naming them with a way to remove them. Rows are rule-table rows
 * ({ id, cells } or { id, fullWidth }, see OptimizerRuleTableParts).
 */
export function buildManualConstructionRows({ constraints, objective, actions, evaluationIndex, disabled, resultStale, baselineLabel = 'Baseline (Current)', activeOnly = false, modeEditor = null, rangeEditor = null, searchRanges = null }) {
    if (!META_OPTIMIZER_ENABLED || (!(objective in MANUAL_OBJECTIVES) && !constraints.design)) return [];
    const design = constraints.design ?? manualDesign(objective === 'PortfolioDesign' ? 'AlphaScore' : objective);
    const mandate = design.mandate;
    const update = (key, value) => actions.setDesign({ ...design, mandate: { ...mandate, [key]: value } });
    const floor = mandate.score_floor;
    const floorEvaluation = evaluationIndex.byRuleId.get('design:conviction_floor');
    const check = floorEvaluation?.detail?.checks?.[0];
    const baselineCheck = floorEvaluation?.detail?.baseline_checks?.[0];
    const baselineScore = baselineCheck?.value ?? evaluationIndex.portfolio?.alpha_score?.baseline;
    const parameter = floor?.mode === 'absolute_musd_score' ? 'design:absolute_score' : 'design:retention_pct';
    const floorEnabled = (on) => update('score_floor', on ? (floor ?? { mode: 'retention_pct', value: 95 }) : null);
    const floorProps = { parameter, label: FLOOR_LABEL, value: floor?.value ?? 95,
        unit: floor?.mode === 'absolute_musd_score' ? 'usd' : 'percent',
        multiplier: floor?.mode === 'absolute_musd_score' ? 1e6 : 1,
        allowNegative: floor?.mode === 'absolute_musd_score', disabled: disabled || !floor,
        onChange: (value) => update('score_floor', { ...floor, value }) };
    // Adapt the design check to the same display contract as ordinary rules.
    // An absent floor in a completed receipt is explicitly Not applied.
    const evaluation = {
        configured: Boolean(floorEvaluation || evaluationIndex.hasResult),
        enabled: Boolean(floorEvaluation?.applied), applied: Boolean(floorEvaluation?.applied),
        status: floorEvaluation?.status ?? 'not_applied',
        limit: check?.lower == null ? null : check.lower * 1e6,
        baseline: baselineScore == null ? null : baselineScore * 1e6,
        optimized: check?.value == null ? null : check.value * 1e6,
    };
    const requestedFloor = !floor ? null : (floor.mode === 'retention_pct'
        ? baselineScore * floor.value / 100 : floor.value) * 1e6;
    const { changed, draftLimit } = ruleEditState(evaluation, {
        enabled: Boolean(floor), value: requestedFloor, explore: Boolean(searchRanges?.[parameter]),
    });
    const rows = [];
    if (!activeOnly || floor) {
        rows.push({ id: 'construction:floor', cells: {
            scope: 'Construction',
            rule: <RuleLabel label={FLOOR_LABEL} unitLabel="minimum conviction" description="Sum of signed position value × trade idea × specific risk. This differs from the unweighted Trade-idea score. Protect aggregate conviction, not investment returns. Percentage retention requires a positive baseline; use an absolute floor otherwise." />,
            mode: ruleModeControl(modeEditor, { parameter, enabled: Boolean(floor), value: floor?.value ?? 95, label: FLOOR_LABEL, disabled,
                onEnabledChange: floorEnabled, switchLabel: `Apply ${FLOOR_LABEL}` }),
            limit: (
                <span className="flex items-center justify-end gap-1.5">
                    <Select value={floor?.mode ?? 'retention_pct'} disabled={disabled || !floor} aria-label={`${FLOOR_LABEL} units`} className="min-w-[140px]" onChange={(e) => update('score_floor', { mode: e.target.value, value: e.target.value === 'retention_pct' ? 95 : 0 })}>
                        <option value="retention_pct">% of baseline</option><option value="absolute_musd_score">Absolute $ score</option>
                    </Select>
                    {rangeEditor ? rangeEditor(floorProps) : <RuleLimitInput {...floorProps} value={floorProps.value * floorProps.multiplier}
                        ariaLabel="Alpha-Score minimum" onChange={(value) => floorProps.onChange(value == null ? null : value / floorProps.multiplier)} />}
                </span>
            ),
            baseline: <RuleValue value={evaluation.baseline} unit="usd" muted={ruleSwitchedOff(evaluation)} />,
            optimized: <RuleValue value={evaluation.optimized} unit="usd" muted={ruleSwitchedOff(evaluation)} />,
            track: <LimitTrack evaluation={evaluation} rule={FLOOR_RULE} label={FLOOR_LABEL} baselineLabel={baselineLabel} draftLimit={draftLimit} />,
            status: <RuleStatusChip evaluation={evaluation} rule={FLOOR_RULE} changed={changed} stale={resultStale} />,
        } });
    }
    if (hasAdditionalProtections(mandate)) {
        rows.push({ id: 'construction:protections', fullWidth: (
            <span className="flex flex-wrap items-center gap-x-2">
                <span className="text-[0.75rem] leading-[1.66]">Additional protections from this saved portfolio remain active: {[
                    mandate.position_band_pct != null ? `position size bands ±${mandate.position_band_pct}%` : null,
                    mandate.preserve_sides ? 'preserve long/short directions' : null,
                    mandate.preserve_gross ? 'preserve gross exposure' : null,
                    mandate.preserve_net ? 'preserve net exposure' : null,
                    mandate.position_bands.length ? `${mandate.position_bands.length} explicit position bounds` : null,
                    mandate.factor_targets.length ? `${mandate.factor_targets.length} signed factor targets` : null,
                    mandate.baskets.length ? `${mandate.baskets.length} thesis baskets` : null,
                    mandate.hedge_sleeve ? 'hedge sleeve' : null,
                    mandate.total_vol_max_pct_aum != null ? `total volatility ≤ ${mandate.total_vol_max_pct_aum}% AUM` : null,
                ].filter(Boolean).join(' · ')}.</span>
                <Button disabled={disabled} onClick={() => actions.setDesign({ ...design, construction_mode: 'resize', mandate: {
                    ...mandate, position_band_pct: null, preserve_sides: false, preserve_gross: false, preserve_net: false,
                    position_bands: [], factor_targets: [], baskets: [], hedge_sleeve: null, total_vol_max_pct_aum: null,
                } })}>Remove additional protections</Button>
            </span>
        ) });
    }
    return rows;
}
